import { CsvFileConfig } from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';

const REGEX_UPPER_SNAKE = '^[A-Z][A-Z0-9_]*$';

const REGEX_PRINTABLE = '^[\\x20-\\x7E]+$';

export const CSV_FILE_CONFIGS: CsvFileConfig[] = [
  {
    name: 'Component Class',
    description:
      'Defines component classes such as MAIN_CONTROLLER, ENERGY_METER, CONNECTOR_MODULE, etc.',
    fileName: 'component_class.csv',
    columns: [
      {
        key: 'code',
        title: 'Code',
        required: true,
        fieldType: 'string',
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE (e.g. MAIN_CONTROLLER)',
      },
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Name must be UPPER_SNAKE_CASE (e.g. MAIN_CONTROLLER)',
      },
      {
        key: 'description',
        title: 'Description',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Description must contain printable characters',
      },
      {
        key: 'synonyms',
        title: 'Synonyms',
        required: false,
        fieldType: 'stringArray',
      },
      {
        key: 'parentCode',
        title: 'Parent Code',
        required: false,
        fieldType: 'string',
        regex: REGEX_UPPER_SNAKE,
        regexMessage:
          'Parent code must be UPPER_SNAKE_CASE (e.g. MAIN_CONTROLLER)',
      },
    ],
  },
  {
    name: 'Asset Type',
    description:
      'Defines asset types like EVSE, BATTERY_STORAGE, SOLAR_INVERTER, HVAC_SYSTEM, etc.',
    fileName: 'asset_type.csv',
    columns: [
      {
        key: 'code',
        title: 'Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage:
          'Code must be UPPER_SNAKE_CASE (e.g. EVSE, BATTERY_STORAGE)',
      },
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'description',
        title: 'Description',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Description must contain only printable characters',
      },
      {
        key: 'metadataSchema',
        title: 'Metadata Schema (JSON)',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Metadata schema must be valid JSON',
        excludeFromCsv: true,
      },
      {
        key: 'associatedSubsystemsCount',
        title: 'Subsystems',
        required: false,
        fieldType: 'integer',
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Subsystem',
    description:
      'Defines subsystems like CHARGE_CONTROL, POWER_DISTRIBUTION, THERMAL_MANAGEMENT, etc.',
    fileName: 'subsystem.csv',
    columns: [
      {
        key: 'code',
        title: 'Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE (e.g. CHARGE_CONTROL)',
      },
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'description',
        title: 'Description',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Description must contain only printable characters',
      },
      {
        key: 'metadataSchema',
        title: 'Metadata Schema (JSON)',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Metadata schema must be valid JSON',
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Event State',
    description:
      'Defines event/state codes such as CONNECTED, DISCONNECTED, FAULTED, CHARGING, etc.',
    fileName: 'event_state.csv',
    columns: [
      {
        key: 'code',
        title: 'Code',
        required: true,
        fieldType: 'string',
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE (e.g. CONNECTED, FAULTED)',
      },
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Name must be UPPER_SNAKE_CASE (e.g. CONNECTED, FAULTED)',
      },
    ],
  },
  {
    name: 'Parameter Name',
    description:
      'Defines parameter names with units: VOLTAGE/VOLT, CURRENT/AMPERE, TEMPERATURE/CELSIUS, etc.',
    fileName: 'parameter_name.csv',
    columns: [
      {
        key: 'code',
        title: 'Code',
        required: true,
        fieldType: 'string',
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE (e.g. CONNECTED, FAULTED)',
      },
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'unit',
        title: 'Unit',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage:
          'Unit must be UPPER_SNAKE_CASE (e.g. VOLT, CELSIUS, AMPERE)',
      },
    ],
  },
  {
    name: 'Component Type',
    description:
      'Defines component types for components with their status and other metadata.',
    fileName: 'component-type.csv',
    columns: [
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'synonyms',
        title: 'Synonyms',
        required: false,
        fieldType: 'stringArray',
      },
      {
        key: 'status',
        title: 'Status',
        required: false,
        options: ['CONFIRMED', 'PENDING'],
      },
      {
        key: 'associatedComponentClassesCount',
        title: 'Component Classes Count',
        required: true,
        fieldType: 'integer',
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Manufacturer',
    description:
      'Catalog of equipment manufacturers (ACTIVE or LEGACY) with an optional log name.',
    fileName: 'manufacturer.csv',
    hideUpload: true,
    columns: [
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'status',
        title: 'Status',
        required: false,
        options: ['ACTIVE', 'LEGACY'],
      },
      {
        key: 'logName',
        title: 'Log Name',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Log name must contain printable characters',
      },
    ],
  },
  {
    name: 'Asset Model',
    description:
      'Asset models belonging to an asset type (name must be unique).',
    fileName: 'asset_model.csv',
    hideUpload: true,
    columns: [
      {
        key: 'name',
        title: 'Name',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Name must contain printable characters',
      },
      {
        key: 'description',
        title: 'Description',
        required: false,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Description must contain printable characters',
      },
      {
        key: 'assetTypeCode',
        title: 'Asset Type Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'assetTypeName',
        title: 'Asset Type Name',
        required: false,
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Asset Type Manufacturer Model',
    description:
      'Associates an asset type with a manufacturer organization and a compatible asset model.',
    fileName: 'asset_type_manufacturer_model.csv',
    hideUpload: true,
    columns: [
      {
        key: 'assetTypeCode',
        title: 'Asset Type Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'manufacturerName',
        title: 'Manufacturer Organization',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage:
          'Manufacturer organization name must contain printable characters',
      },
      {
        key: 'assetModelName',
        title: 'Asset Model',
        required: true,
        regex: REGEX_PRINTABLE,
        regexMessage: 'Asset model name must contain printable characters',
      },
      {
        key: 'assetTypeName',
        title: 'Asset Type Name',
        required: false,
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Asset-Subsystem-ComponentClass Mapping',
    description:
      'Manages the component classes assigned to each Asset Type and Subsystem pair.',
    fileName: 'asset_subsystem_componentclass_mapping.csv',
    hideUpload: true,
    readonly: true,
    columns: [
      {
        key: 'assetTypeCode',
        title: 'Asset Type Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'subsystemCode',
        title: 'Subsystem Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'associatedComponentClassCount',
        title: 'Component Classes Count',
        required: true,
        fieldType: 'integer',
        excludeFromCsv: true,
      },
    ],
  },
  {
    name: 'Asset-Subsystem-ComponentClass-ComponentType Mapping',
    description:
      'Manages the component types assigned to each Asset Type / Subsystem / Component Class combination.',
    fileName: 'asset_subsystem_componentclass_component_type_mapping.csv',
    hideUpload: true,
    readonly: true,
    columns: [
      {
        key: 'assetTypeCode',
        title: 'Asset Type Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'subsystemCode',
        title: 'Subsystem Code',
        required: true,
        regex: REGEX_UPPER_SNAKE,
        regexMessage: 'Code must be UPPER_SNAKE_CASE',
      },
      {
        key: 'componentClassCode',
        title: 'Component Class Code',
        required: true,
      },
      {
        key: 'associatedComponentTypesCount',
        title: 'Component Types',
        required: false,
        fieldType: 'integer',
        excludeFromCsv: true,
      },
    ],
  },
];
